/**
 * asr-quality-guard — decides whether a Whisper result is degenerate output
 * rather than a faithful transcript of quiet audio.
 *
 * Loaded by whisper-worker.js via importScripts (auto-captions, redub,
 * video-dubbing all use that worker), and unit-tested directly in node.
 *
 * Why a density rule was added (measured 2026-07-23, 20 days of prod runs):
 * the old detector caught only the loud failures — empty text, a single chunk
 * for long audio, or a punctuation-only string. It missed the quiet one: a few
 * plausible words spread over several chunks. Prod:
 *
 *   sparse-result rate   WebGPU 37%  vs  WASM 8%     (runs with media > 25 s)
 *   words per second of audio Whisper actually heard:
 *     WebGPU  p10=0.046  p25=0.082  p50=1.011        <- bimodal
 *     WASM    p10=0.457  p25=1.106  p50=1.760        <- no such cluster
 *
 * A third of WebGPU runs sit around 0.05-0.08 tokens/s while healthy speech
 * runs at ~1-2. That is a gap, not a continuum — the mark of a broken decode,
 * consistent with the worker's own note that WebGPU fp16 degeneracy is
 * non-deterministic. Sweeping the threshold over the same dataset:
 *
 *   threshold   WebGPU fires   WASM fires (control: same files, sound decoder)
 *     0.10         29%             1%
 *     0.25         37%             2%
 *     0.40         41%             7%
 *
 * 0.25 sits on the flat part — recall is already maxed and the control rate has
 * not started climbing. ~94% of fires are excess over the WASM baseline, i.e.
 * runs that were broken rather than quiet.
 *
 * Two guards against firing on honest silence:
 *   - `MIN_JUDGE_SEC` (45 s): below that a low count means nothing.
 *   - Whisper's own non-speech markers ("[Music]", "(soft music)", "♪") are a
 *     CONFIDENT statement that there is nothing to transcribe — not a broken
 *     decode. Verified on prod: a 5-minute music file returns exactly
 *     "[Music] (soft music)", and re-running it on WASM would return the same.
 *
 * Token counting is script-aware: CJK and Korean write without spaces, so
 * splitting on whitespace would make every Chinese/Japanese transcript look
 * degenerate. Each CJK/Kana/Hangul character counts as one token.
 *
 * Exposed as `self.__asrQualityGuard` (worker) + module.exports (node tests).
 */
(function () {
    'use strict';

    var MIN_JUDGE_SEC = 45;      // shorter audio can legitimately be near-silent
    var MIN_TOKENS_PER_SEC = 0.25;

    /* ── The denominator that collapses ─────────────────────────────────────
       Everything above judges density against the audio Whisper actually HEARD
       — VAD-packed, speech-only. That base is right for "did the decoder work",
       and wrong in one specific, common way: it SHRINKS as the input gets worse.
       VAD keeps 1 s of a 147 s video, Whisper returns 4 words, and the density
       reads 4.0 tokens/s — healthier than real speech — while the user is
       looking at a video with no subtitles. Measured on prod (auto-captions,
       07-01…08-19, 1133 judged runs): of the runs that ended useless for the
       user, 33 sailed past this guard entirely, and their misses are exactly
       this shape (4 words/147 s heard 1 s · 1 word/180 s heard 3 s · 2 words/
       52 s heard 8 s). Same class as the noise-floor lesson: a threshold that
       gets MORE forgiving precisely when things get worse.

       So the verdict takes a SECOND rate when the caller can supply the media
       duration — words per second of THEIR file, the number they perceive —
       and the pair separates two failures that need opposite remedies:

         speech is there (VAD kept a normal share) + almost no words
             → OUR decode broke        → garbage:true  → re-run on WASM
         VAD kept almost nothing       + almost no words
             → the file has no speech  → thin:true     → tell them, never re-run
                                                         (a re-run returns the
                                                          same nothing, slowly)

       Thresholds are the ones auto-captions-plausibility.js already measured
       and shipped for the page-level verdict; this is the same rule moved to
       where it can also drive the recovery, so the two cannot drift apart. */
    var MIN_TOKENS_PER_MEDIA_SEC = 0.06;   // ~4 words a minute = "no subtitles"
    var LOW_SPEECH_RATIO = 0.12;           // VAD kept <12% of the media

    var CJK_RX = /[぀-ヿ㐀-䶿一-鿿가-힯]/g;
    var WORD_RX = /[\p{L}\p{N}][\p{L}\p{N}'’\-]*/gu;
    /* Whisper's own "there is no speech here" vocabulary: bracketed event
       markers and musical notes. Must be narrow — the classic degenerate
       signature (". /, /, /,. / / /") is ALSO punctuation-only but means the
       decoder broke, so plain punctuation alone never counts as an answer. */
    var NONSPEECH_MARK_RX = /[♪♫]|[\[(][^\])]{0,40}[\])]/;
    var NONSPEECH_ONLY_RX = /^(?:\s|[♪♫]|[\[(][^\])]{0,40}[\])]|[.,!?…"'«»—–-])+$/;

    /* ── Вырождение ВТОРОГО рода: петля ────────────────────────────────────
       Правило плотности выше ловит «модель замолчала». Оно СЛЕПО к обратному
       отказу: модель говорит без умолку, но одно и то же. Замер 28.08 на
       /lyrics-transcription, английская песня, whisper-small на webgpu fp16 —
       выдача «I hope you see my face.» повтором:

           плотность 0.74 слова/с   ← ВЫШЕ порога 0.25, правило молчит
           доля уникальных слов 0.20
           доля самой частой 4-граммы 0.085

       Три прогона дали побайтово одинаковый результат, то есть это свойство
       пути, а не случайность. Механизм восстановления в whisper-worker.js уже
       был (вердикт → травим WebGPU → перезагрузка на WASM → перепрогон); не
       хватало только того, чтобы вердикт ЭТО УВИДЕЛ.

       Пороги выведены из трёх выборок, а не выбраны:

       | выборка                                   |  n  | уникальных | топ-4-грамма |
       |-------------------------------------------|-----|------------|--------------|
       | речь (whisper base/small/turbo, ru+en+ko) |   9 | 0.45–0.87  | 0.006–0.009  |
       | ПЕСНИ, выдача по делу (9 языков, развёртка)| 148 | 0.25–0.87  | ≤ 0.062      |
       | вырожденные петли (webgpu fp16)           |   3 | 0.20       | 0.085        |

       Песни — трудный случай для такого правила: припев повторяется по
       устройству жанра. Поэтому условия ДВА и оба обязательны; по решётке
       порогов 0.22–0.30 × 0.065–0.080 ложных срабатываний ноль, поймано 3 из 3.
       Одиночного условия не хватило бы: у самой «бедной» здоровой песни
       уникальных 0.25, то есть по одной уникальности зазора почти нет.
       ⚠️ Прод-фикстура `ac-prod-runs-*.json` этому правилу НЕ судья: в ней
       только счётчики слов, текста нет. Ложные срабатывания проверяются
       текстовыми выборками выше. */
    var LOOP_MIN_TOKENS = 60;          // короче — статистика ни о чём
    var LOOP_MAX_UNIQUE_RATIO = 0.28;  // здоровая песня ≥ 0.25, петля 0.20
    var LOOP_MIN_TOP_NGRAM = 0.07;     // здоровая песня ≤ 0.062, петля 0.085
    var LOOP_NGRAM = 4;

    /* Слова для статистики повторов. CJK/корейский пишутся без пробелов —
       считаем их посимвольно, как и countTokens, иначе китайская выдача
       выглядит как одно длинное «слово» и правило не работает вовсе. */
    function loopTokens(text) {
        var s = String(text || '').toLowerCase();
        var cjk = s.match(CJK_RX) || [];
        var rest = (s.replace(CJK_RX, ' ').match(WORD_RX) || []);
        return rest.concat(cjk);
    }

    /* Доля уникальных слов и доля самой частой N-граммы. Оба числа
       безразмерные, поэтому не зависят от длины файла. */
    function repetitionStats(text) {
        var w = loopTokens(text);
        if (w.length < LOOP_MIN_TOKENS) return null;
        var seen = Object.create(null), uniq = 0, i;
        for (i = 0; i < w.length; i++) { if (!seen[w[i]]) { seen[w[i]] = 1; uniq++; } }
        var counts = Object.create(null), top = 0, total = w.length - LOOP_NGRAM + 1;
        for (i = 0; i + LOOP_NGRAM <= w.length; i++) {
            var k = w.slice(i, i + LOOP_NGRAM).join(' ');
            counts[k] = (counts[k] || 0) + 1;
            if (counts[k] > top) top = counts[k];
        }
        return { uniqueRatio: uniq / w.length, topNgramShare: total > 0 ? top / total : 0, words: w.length };
    }

    function looksLikeLoop(text) {
        var r = repetitionStats(text);
        if (!r) return null;
        r.loop = r.uniqueRatio < LOOP_MAX_UNIQUE_RATIO && r.topNgramShare > LOOP_MIN_TOP_NGRAM;
        return r;
    }

    function countTokens(text) {
        if (!text) return 0;
        var s = String(text);
        var cjk = (s.match(CJK_RX) || []).length;
        var rest = s.replace(CJK_RX, ' ');
        return cjk + ((rest.match(WORD_RX) || []).length);
    }

    /* True when Whisper is confidently reporting "no speech here" rather than
       failing. Re-running these on another backend returns the same thing. */
    function looksLikeNonSpeechMarkers(text) {
        var s = String(text || '').trim();
        if (!s) return false;
        return NONSPEECH_MARK_RX.test(s) && NONSPEECH_ONLY_RX.test(s);
    }

    /**
     * @param result  Whisper pipeline result ({ text, chunks })
     * @param durSec  seconds of audio the model actually heard (VAD-packed
     *                audio is already speech-only, so this is the right base)
     * @param opts    { mediaSec } — the ORIGINAL media duration, when the caller
     *                knows it. Supplying it enables the second rate and the
     *                garbage/thin split; omitting it yields exactly the verdict
     *                this function gave before mediaVerdict existed.
     * @returns {{garbage:boolean, thin:boolean, reason:string, tokens:number,
     *           tokensPerSec:number, mediaSec?:number, tokensPerMediaSec?:number,
     *           speechRatio?:number}}
     *          garbage → our decode broke; re-run on the other backend.
     *          thin    → the file carries no speech; tell the user, do not re-run.
     */
    function judge(result, durSec, opts) {
        var minSec = (opts && opts.minJudgeSec != null) ? opts.minJudgeSec : MIN_JUDGE_SEC;
        var minTps = (opts && opts.minTokensPerSec != null) ? opts.minTokensPerSec : MIN_TOKENS_PER_SEC;
        var out = { garbage: false, thin: false, reason: '', tokens: 0, tokensPerSec: 0 };
        try {
            var chunks = (result && result.chunks) || [];
            var text = ((result && result.text) || '').trim();
            var dur = +durSec || 0;

            out.tokens = countTokens(text);
            out.tokensPerSec = dur > 0 ? out.tokens / dur : 0;

            if (dur > minSec && chunks.length <= 1) { out.reason = 'single_chunk'; out.garbage = true; return out; }
            if (!text) { out.reason = 'empty_text'; out.garbage = true; return out; }

            // Checked BEFORE the punctuation rule: "♪ ♪ ♪" is letter-free yet is
            // Whisper answering, not failing — a re-run returns the same notes.
            // It is also the answer "your file has no speech", so it leaves as a
            // THIN verdict the page can explain, not as a silent empty result.
            if (looksLikeNonSpeechMarkers(text)) { out.reason = 'non_speech_markers'; out.thin = true; return mediaVerdict(out, text, dur, opts); }

            // Short HEARD audio is not short MEDIA. VAD packs a three-minute
            // video down to the seconds it believes are speech, and this early
            // exit is precisely where the collapsed-denominator runs used to
            // leave: 4 words for a 147 s video, dismissed as "too short to
            // judge" because the model had been handed one second. The content
            // rules below still stand down; the media rate gets its say.
            if (dur <= 5) return mediaVerdict(out, text, dur, opts);

            var letters = (text.match(/[\p{L}\p{N}]/gu) || []).length;
            if ((letters / text.length) < 0.35) { out.reason = 'mostly_punctuation'; out.garbage = true; return out; }
            /* Петля: говорит много, но одно и то же. Проверяется ДО правила
               плотности — у петли плотность нормальная, и без этого правила
               выдача уходит пользователю как его текст. */
            var loop = looksLikeLoop(text);
            if (loop) {
                out.uniqueRatio = +loop.uniqueRatio.toFixed(3);
                out.topNgramShare = +loop.topNgramShare.toFixed(3);
                if (loop.loop && !looksLikeNonSpeechMarkers(text)) {
                    out.reason = 'repetition_loop';
                    out.garbage = true;
                    return out;
                }
            }
            if (dur >= minSec && out.tokensPerSec < minTps) {
                // Confident "there is no speech" is an answer, not a failure —
                // and an answer the page owes the user, so it leaves as thin.
                if (looksLikeNonSpeechMarkers(text)) { out.reason = 'non_speech_markers'; out.thin = true; return mediaVerdict(out, text, dur, opts); }
                out.reason = 'too_few_tokens';
                out.garbage = true;
            }
            return mediaVerdict(out, text, dur, opts);
        } catch (e) { return out; }
    }

    /**
     * Second rate: words per second of the USER'S media, and the split between
     * "we broke" and "there is nothing here". No-op unless the caller supplies
     * `opts.mediaSec` — callers that don't know the media duration keep exactly
     * the verdict they got before this existed.
     */
    function mediaVerdict(out, text, heardSec, opts) {
        var mediaSec = +((opts && opts.mediaSec) || 0);
        var minSec = (opts && opts.minJudgeSec != null) ? opts.minJudgeSec : MIN_JUDGE_SEC;
        var minPerMedia = (opts && opts.minTokensPerMediaSec != null)
            ? opts.minTokensPerMediaSec : MIN_TOKENS_PER_MEDIA_SEC;
        if (!(mediaSec >= minSec)) return out;          // nothing to add / too short to judge

        out.mediaSec = mediaSec;
        out.tokensPerMediaSec = out.tokens / mediaSec;
        out.speechRatio = Math.min(1, heardSec / mediaSec);
        /* ⚠️ speechRatio only CARRIES INFORMATION when VAD packed the audio.
           With no VAD the model was handed the whole file, the ratio is 1.0 by
           construction, and reading it as "speech was present" would turn every
           broken decode into "your file has no speech" — blaming the user for
           our failure, which is worse than saying nothing. Callers may state it
           outright; otherwise packing is inferred from the buffer having been
           shortened, which is exactly what packing does. */
        out.vadUsed = (opts && opts.vadUsed != null) ? !!opts.vadUsed : (heardSec < mediaSec * 0.995);
        if (out.tokensPerMediaSec >= minPerMedia) return out;

        // "[Music]" is Whisper answering, and the answer is the honest one.
        if (looksLikeNonSpeechMarkers(text)) { out.garbage = false; out.thin = true; out.reason = 'non_speech_markers'; return out; }

        if (out.vadUsed && out.speechRatio < LOW_SPEECH_RATIO) {
            // Almost nothing in the file was speech. Re-running the decoder
            // returns the same handful of words after the same long wait — the
            // useful move is to say so, and to say what the file IS.
            out.garbage = false;
            out.thin = true;
            out.reason = 'little_speech_in_media';
            return out;
        }
        if (!out.vadUsed) {
            // No packing happened, so there is no evidence either way. Keep the
            // verdict the heard-rate already reached (a re-run is the right
            // default for the WebGPU degeneracy class) and add nothing we cannot
            // support — the media numbers are reported, the claim is not made.
            return out;
        }
        // VAD kept a normal share of the file as speech and the words are still
        // not there. That is ours to fix, by re-run.
        out.garbage = true;
        out.thin = false;
        if (out.reason !== 'too_few_tokens') out.reason = 'few_words_for_media';
        return out;
    }

    /* ── Dead decode, caught at the source ──────────────────────────────────
       Everything above judges FINISHED text. That is one step too late and one
       layer too high: by the time a transcript can be judged, the degenerate
       tokens have already been streamed to the screen and written to the resume
       checkpoint, and on a run that never finishes (phone backgrounds the tab)
       nothing is ever judged at all. Support case 2026-08-16 (#2680) is exactly
       that shape — a 4-minute song came back as walls of "!" around a few real
       words, and no recovery fired because the run never reached the end.

       The mechanism, measured in node against the real base model 2026-08-17
       (scratchpad E2/E6, whisper-base, chanson.mp3):

         • The decoder's logits row goes **entirely NaN** — 51865 of 51865 —
           from decode step 0. Healthy rows carry zero NaN, ever.
         • Greedy argmax is `let best = d[0]; for (…) if (d[i] > best) …`.
           Every `NaN > NaN` is false, so it returns index 0. Whisper token 0
           is "!". The window then runs to its 448-token cap emitting "!".
           445 characters per window — the exact block size in the screenshot.
         • No logits-level setting can help: banning token 0 writes -Infinity
           into it, and `NaN > -Infinity` is ALSO false, so argmax still
           answers 0. Verified directly — `no_repeat_ngram_size: 6` and
           `repetition_penalty: 1.15` were active in every reproduction and
           produced a run of 890 identical token-0 steps. Those two options are
           right for their own job (semantic phrase loops on music) and simply
           cannot reach this one.
         • fp16 is the fragile lane: with one second of NaN in the input, q8
           still returned " [Music] [Music]" while fp16 collapsed to 445 "!".
           WebGPU is always fp16 here, which is why the failure clusters there.

       So the honest check is for NaN itself, and it is O(1): the row is either
       wholly NaN or wholly finite. Two probes (head + middle) guard against a
       hypothetical partial collapse without walking 51865 floats per token.
       Cost is below measurement noise (median +111 ms on a 7.4 s run whose own
       run-to-run spread was 5.4–19.4 s).

       `stopping_criteria` is NOT the lever for this — transformers.js 3.8.1's
       WhisperForConditionalGeneration.generate() destructures the option and
       never forwards it to super.generate(), so it is silently inert. Measured:
       the interrupt fired at step 24 and the decode still ran all 601 steps.
       `logits_processor` IS forwarded, so the tripwire lives there. */
    function isDeadLogits(data) {
        if (!data || !data.length) return false;
        return Number.isNaN(data[0]) || Number.isNaN(data[data.length >> 1]);
    }

    /* Chunk-level verdict for the STREAMING path — the page calls this before
       it paints a chunk or writes it into the resume checkpoint. Deliberately
       narrower than judge(): a single window carries no duration context, so
       only self-evident garbage is rejected.

         a) a run of identical characters no language produces. Measured
            ceiling for healthy output is 2 consecutive identical TOKENS across
            16 varied files (14 song genres + speech, 816 decode steps of dense
            speech); the degenerate runs are 445-890. MIN_DEAD_RUN = 24 sits
            ~12x above the healthy ceiling and ~18x below the failure floor.
         b) text long enough to judge that is almost entirely non-letters —
            the same 0.35 ratio judge() uses. Healthy minimum measured across
            the same corpus was 0.622.

       Whisper's own non-speech markers ("[Music]", "♪") are an ANSWER, not a
       failure, and are let through untouched. */
    var MIN_DEAD_RUN = 24;
    var MIN_JUDGE_CHARS = 24;
    var DEAD_RUN_RX = /(.)\1{23,}/;

    function chunkLooksDead(text) {
        var s = String(text || '');
        if (!s.trim()) return false;                       // empty is not garbage
        if (DEAD_RUN_RX.test(s)) return true;
        if (looksLikeNonSpeechMarkers(s)) return false;
        if (s.length < MIN_JUDGE_CHARS) return false;      // too short to judge
        var letters = (s.match(/[\p{L}\p{N}]/gu) || []).length;
        return (letters / s.length) < 0.35;
    }

    var api = {
        judge: judge,
        countTokens: countTokens,
        looksLikeNonSpeechMarkers: looksLikeNonSpeechMarkers,
        isDeadLogits: isDeadLogits,
        chunkLooksDead: chunkLooksDead,
        repetitionStats: repetitionStats,
        looksLikeLoop: looksLikeLoop,
        LOOP_MAX_UNIQUE_RATIO: LOOP_MAX_UNIQUE_RATIO,
        LOOP_MIN_TOP_NGRAM: LOOP_MIN_TOP_NGRAM,
        LOOP_MIN_TOKENS: LOOP_MIN_TOKENS,
        MIN_JUDGE_SEC: MIN_JUDGE_SEC,
        MIN_TOKENS_PER_SEC: MIN_TOKENS_PER_SEC,
        MIN_TOKENS_PER_MEDIA_SEC: MIN_TOKENS_PER_MEDIA_SEC,
        LOW_SPEECH_RATIO: LOW_SPEECH_RATIO,
        MIN_DEAD_RUN: MIN_DEAD_RUN,
    };
    if (typeof self !== 'undefined') self.__asrQualityGuard = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
